import { z } from 'zod';

const address = z.string().min(1).max(100);
const observationViewSchema = z.record(z.unknown()).superRefine((view, ctx) => {
  const pending: Array<{ value: unknown; depth: number }> = [{ value: view, depth: 0 }];
  let nodes = 0;
  while (pending.length) {
    const { value, depth } = pending.pop()!;
    if (++nodes > 5000 || depth > 12 || typeof value === 'number' && !Number.isFinite(value) ||
      value !== null && !['object', 'string', 'number', 'boolean'].includes(typeof value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid observation view' });
      return;
    }
    if (value && typeof value === 'object') {
      for (const child of Object.values(value)) pending.push({ value: child, depth: depth + 1 });
    }
  }
});
export const observationCellSchema = z.object({ rowId: address, field: address });
export const observationDraftSchema = observationCellSchema.extend({
  text: z.string().max(20_000),
  selectionStart: z.number().int().min(0).max(20_000).optional(),
  selectionEnd: z.number().int().min(0).max(20_000).optional(),
});

/** Ephemeral display state. Drafts are never committed through this protocol. */
export const tableObservationSchema = z.object({
  surface: z.enum(['candidates', 'sheet']),
  sheetCode: z.string().min(1).max(100).optional(),
  // Each table owns its view schema; this bounded JSON only goes to administrators.
  view: observationViewSchema,
  viewport: z.object({
    rowId: address.nullable(),
    field: address.nullable(),
    offsetY: z.number().finite().min(-1_000_000_000).max(1_000_000_000),
    offsetX: z.number().finite().min(-1_000_000_000).max(1_000_000_000),
    scrollTop: z.number().finite().min(0).max(1_000_000_000),
    scrollLeft: z.number().finite().min(0).max(1_000_000_000),
    rowIds: z.array(address).max(300),
  }),
  active: observationCellSchema.nullable(),
  selection: z.object({
    anchor: observationCellSchema,
    focus: observationCellSchema,
    rowIds: z.array(address).max(300),
    fields: z.array(address).max(200),
  }).nullable(),
  draft: observationDraftSchema.nullable(),
}).superRefine((state, ctx) => {
  if (state.surface === 'sheet' && !state.sheetCode) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Sheet code required', path: ['sheetCode'] });
  }
  let size = Infinity;
  try { size = JSON.stringify(state).length; } catch { /* Cyclic/non-JSON input is invalid. */ }
  if (size > 100_000) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Observation state too large' });
  }
});

export type ObservationDraft = z.infer<typeof observationDraftSchema>;
export type TableObservation = z.infer<typeof tableObservationSchema>;

const observationId = z.string().min(1).max(100).regex(/^[a-zA-Z0-9_-]+$/);
export const clientObservationMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('observation.publish'), tabId: observationId, state: tableObservationSchema.nullable() }),
  z.object({ type: z.literal('observation.subscribe'), userId: observationId, tabId: observationId.optional() }),
  z.object({ type: z.literal('observation.unsubscribe') }),
]);

export type ClientObservationMessage =
  | { type: 'observation.publish'; tabId: string; state: TableObservation | null }
  | { type: 'observation.subscribe'; userId: string; tabId?: string }
  | { type: 'observation.unsubscribe' };

export interface ServerObservationState {
  type: 'observation.state';
  userId: string;
  tabId: string | null;
  name: string;
  color: string;
  status: 'live' | 'waiting' | 'offline' | 'forbidden';
  revision: number;
  state: TableObservation | null;
}
